import { useState, type ReactNode } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { DegreeField, DegreePair, DegreePairFields, ReferenceClimate } from './DegreeFields';
import { PerfTable } from './PerfTable';
import { RatioImport } from './RatioImport';
import { RatioSectionHeader } from './RatioSectionHeader';
import { cloneRatios, countModifiedRatios, isValidDjclim, isValidDju } from './domain';
import { parseNum } from './sheet';
import type { DegreeDays, RatioTable as Ratios } from './types';

interface Props {
  provenance: string;
  initialization?: ReactNode;
  /** Degrés-jours auxquels mes ratios ont été établis — sans lien avec la station du projet. */
  degrees: DegreeDays;
  ratios: Ratios;
  /** Référentiel de démonstration, proposé par défaut pour aider à la saisie. */
  defaults: Ratios;
  defaultDegrees: DegreeDays;
  onDegreesChange: (next: DegreeDays) => void;
  onRatiosChange: (next: Ratios) => void;
  onValidityChange: (ok: boolean) => void;
  /** Mode saisie remonté au parent : il désactive l'application au projet. */
  onEditingChange: (editing: boolean) => void;
}

/** Encart « Mes ratios » : import d'un fichier ou saisie manuelle, DJ et ratios modifiables ensemble. */
export function MyRatiosCard(p: Props) {
  const [editing, setEditing] = useState(false);
  const [dju, setDju] = useState(String(p.degrees.dju));
  const [djclim, setDjclim] = useState(String(p.degrees.djclim));
  const [tableOk, setTableOk] = useState(true);
  const [beforeEdit, setBeforeEdit] = useState<{ degrees: DegreeDays; ratios: Ratios } | null>(null);

  const djuValid = isValidDju(parseNum(dju));
  const climValid = isValidDjclim(parseNum(djclim));
  const valid = tableOk && djuValid && climValid;
  const modified = countModifiedRatios(p.ratios, p.defaults);
  const djChanged = p.degrees.dju !== p.defaultDegrees.dju || p.degrees.djclim !== p.defaultDegrees.djclim;
  const source = p.provenance.replace(/^Source : /, '');
  const perimeterZone = /^Ratios par zone climatique — ([^,]+),/.exec(source)?.[1];
  const reference = {
    zone: p.ratios.climateZone,
    source: !p.ratios.climateZone ? 'Zone climatique de référence indéterminée.'
      : perimeterZone && perimeterZone !== p.ratios.climateZone
        ? `Ratios par zone climatique — ${p.ratios.climateZone}. Communes sélectionnées : ${source.split(' : ')[1]}`
        : source,
  };
  const setMode = (on: boolean) => { setEditing(on); p.onEditingChange(on); };

  const emit = (ok: boolean) => {
    setTableOk(ok);
    p.onValidityChange(ok && djuValid && climValid);
  };
  const open = () => {
    setBeforeEdit({ degrees: { ...p.degrees }, ratios: cloneRatios(p.ratios) });
    setDju(String(p.degrees.dju)); setDjclim(String(p.degrees.djclim)); setMode(true);
  };
  const cancel = () => {
    if (!beforeEdit) return;
    p.onDegreesChange(beforeEdit.degrees);
    p.onRatiosChange(beforeEdit.ratios);
    setDju(String(beforeEdit.degrees.dju)); setDjclim(String(beforeEdit.degrees.djclim));
    setTableOk(true);
    p.onValidityChange(true); setMode(false);
  };
  const editDju = (v: string) => {
    setDju(v);
    const n = parseNum(v);
    if (isValidDju(n)) p.onDegreesChange({ ...p.degrees, dju: n });
    p.onValidityChange(tableOk && isValidDju(n) && climValid);
  };
  const editDjclim = (v: string) => {
    setDjclim(v);
    const n = parseNum(v);
    if (isValidDjclim(n)) p.onDegreesChange({ ...p.degrees, djclim: n });
    p.onValidityChange(tableOk && djuValid && isValidDjclim(n));
  };
  const imported = (degrees: DegreeDays, ratios: Ratios) => {
    p.onDegreesChange(degrees); p.onRatiosChange(ratios);
    setDju(String(degrees.dju)); setDjclim(String(degrees.djclim));
  };
  const resetAll = () => {
    p.onDegreesChange(p.defaultDegrees); p.onRatiosChange(p.defaults);
    setDju(String(p.defaultDegrees.dju)); setDjclim(String(p.defaultDegrees.djclim));
    emit(true);
  };

  return (
    <section className="pcv3-ratios-card" aria-label="Ratios de référence du calcul">
      <RatioSectionHeader number={1} title="Ratios de référence" icon="tune" />
      <div className="pcv3-ratios-body">
        {!p.provenance.startsWith('Source :') && <NJText role="status">{p.provenance}</NJText>}
        {p.initialization}
        <div className="pcv3-ratios-input-actions">
          {editing ? (
            <>
              <NJButton disabled={!valid} onClick={() => setMode(false)} label="Valider" />
              <NJButton variant="secondary" onClick={cancel} label="Annuler" />
              <NJButton variant="secondary" emphasis="minimal" icon="undo" onClick={resetAll} label="Valeurs par défaut" />
              {!valid && <NJText scale="sm" className="pcv3-ratios-error">Corrigez les valeurs hors plage avant de valider.</NJText>}
            </>
          ) : (
            <>
              <NJButton variant="secondary" icon="edit" onClick={open} label="Modifier" />
              <RatioImport degrees={p.degrees} ratios={p.ratios} onImport={imported} compact />
              {(modified > 0 || djChanged) && <NJButton variant="secondary" emphasis="minimal" icon="undo" onClick={resetAll} label="Valeurs par défaut" />}
            </>
          )}
        </div>
        {editing ? (
          <DegreePairFields reference={reference}>
            <DegreeField label="DJU (chauffage)" base={p.ratios.periodSchema === 'climate2026' ? 18 : undefined} text={dju} valid={djuValid} range="500 à 5 000" onChange={editDju} />
            <DegreeField label="DJClim (froid)" base={p.ratios.periodSchema === 'climate2026' ? 15 : undefined} text={djclim} valid={climValid} range="10 à 1 000" onChange={editDjclim} />
            <ReferenceClimate {...reference} />
          </DegreePairFields>
        ) : (
          <DegreePair dju={p.degrees.dju} djclim={p.degrees.djclim} reference={reference} climateBases={p.ratios.periodSchema === 'climate2026'} hint="Degrés-jours de mes ratios" highlight={djChanged} />
        )}

        <PerfTable
          value={p.ratios}
          defaults={p.defaults}
          editing={editing}
          onChange={p.onRatiosChange}
          onValidityChange={emit}
        />
      </div>
    </section>
  );
}
